import { z } from "zod";
import type { DossierEvidence } from "../schemas";

const HealthResponse = z.object({
  ok: z.literal(true),
  version: z.literal("flurry-hosted/v1"),
  mode: z.literal("hosted-read-only"),
  dossierBackend: z.enum(["anthropic", "evidence-engine"]),
  sources: z.object({
    avalanche: z.object({ state: z.string(), updatedAt: z.string().nullable() }),
    robinhood: z.object({ state: z.string(), updatedAt: z.string().nullable() }),
  }),
});
export type HealthResult = z.infer<typeof HealthResponse>;

const DossierResponse = z.object({
  dossier: z.string().min(1).max(2_000),
  backend: z.enum(["anthropic", "evidence-engine"]),
});

function hostedUrl(path: string): string {
  const base = import.meta.env.BASE_URL.endsWith("/")
    ? import.meta.env.BASE_URL
    : `${import.meta.env.BASE_URL}/`;
  return `${base}api/${path.replace(/^\/+/, "")}`;
}

export async function checkBridge(): Promise<HealthResult> {
  const response = await fetch(hostedUrl("v1/health"), { headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`hosted service health failed: HTTP ${response.status}`);
  return HealthResponse.parse(await response.json());
}

/** Sends only the fixed evidence object to Flurry's same-origin hosted dossier route. */
export async function runDossierViaBridge(evidence: DossierEvidence): Promise<string> {
  const response = await fetch(hostedUrl("v1/dossier"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(evidence),
  });
  if (!response.ok) {
    throw new Error(
      response.status === 429
        ? "hosted dossier is busy — try again shortly"
        : "hosted dossier is temporarily unavailable",
    );
  }
  return DossierResponse.parse(await response.json()).dossier;
}
